/**
 * Confidence is a PERCENT (90 / 95 / 99) everywhere in the crosstabs workspace —
 * the rail edits a percent, every on-screen readout prints a percent, and the
 * significance legend in the Excel export prints a percent.
 *
 * Three `applyLayout` call sites still hand it a fraction (`0.95`), which is how
 * the export came to read "9500% confidence": the legend multiplied an already-
 * percentage value by 100. These helpers make percent the only unit by coercing
 * at the settings-write boundaries, so no fraction can reach the UI or the export,
 * and leave the legend a strict printer of whatever it is given.
 *
 * Nothing here imports exceljs, pptxgenjs or React, so it is unit-testable.
 */

import {
    SIG_CORRECTION_LABELS,
    toSigCorrection,
    type SigCorrection,
} from '@/lib/crosstab/sig-correction';
import { sigMarksOf } from '@/lib/crosstab/sig-marks';
import { IOOH_STANDARD } from '@/lib/crosstab/stats-standard';
import { lowBaseLegendLine } from '@/lib/crosstab/low-base';
import { DEFAULT_SUPPRESSION } from '@/lib/crosstab/base-thresholds';

export interface ConfidencePair {
    confidence: number;
    confidence2: number | null;
}

/**
 * The significance an iOOH Brand Lift crosstab tests at: UPPERCASE letters at 95%, lowercase at
 * 90%, as the iOOH banner plan letters its tables (`docs/pdlc/iooh-tpbp-spec.md`) and the
 * hand-built workbooks print them. Every difference significant at 90%, the deck's threshold, still
 * gets a letter. Allyson Wehn and Jonathan Pruitt, 2026-10-06.
 *
 * It is the default on an iOOH study wherever a crosstab has no level of its own in this browser
 * (`readBrowserFields`, `browserFieldsForRecord`), and the level the DANI Brand Lift setup and a
 * banner-plan upload apply. Every other crosstab starts on `DEFAULT_SETTINGS`: 95%, no second level.
 */
export const IOOH_CONFIDENCE: Readonly<ConfidencePair> = Object.freeze({
    // M4S-5777 X67: read off the iOOH statistics standard, so the two can never disagree.
    confidence: IOOH_STANDARD.confidence,
    confidence2: IOOH_STANDARD.confidence2,
});

/**
 * Coerce a confidence to a percent.
 *
 * A value in (0, 1] is read as a fraction and scaled; anything above 1 is already
 * a percent and is only rounded. The magnitude heuristic is safe by construction:
 * ConfigRail offers 90 / 95 / 99 only, and `confFromP` (src/lib/stats/two-proportion.ts)
 * can only return 0, 90, 95 or 99 — no legitimate confidence can sit in (0, 1].
 *
 * 0 (the "not significant" bucket) and non-numbers fall back rather than becoming 0%.
 */
export function toConfidencePct(v: unknown, fallback: number): number {
    if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return fallback;
    return v <= 1 ? Math.round(v * 100) : Math.round(v);
}

/**
 * The secondary confidence is legitimately absent, so it is modelled as `number | null`
 * and is NEVER its own fallback — a null, a 0 or a NaN becomes `null`, so `if (conf2)`
 * downstream can never behave on a NaN.
 */
export function toConfidence2Pct(v: unknown): number | null {
    if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return null;
    return v <= 1 ? Math.round(v * 100) : Math.round(v);
}

/**
 * The settings merge `applyLayout` performs, lifted out so the unit coercion is
 * assertable. Preserves the previous `layout.confidence || prev.confidence`
 * semantics exactly: a missing or zero confidence keeps the previous value.
 */
export function normalizeLayoutConfidence(
    layout: { confidence?: number | null; confidence2?: number | null },
    prev: ConfidencePair,
): ConfidencePair {
    return {
        confidence: layout.confidence
            ? toConfidencePct(layout.confidence, prev.confidence)
            : prev.confidence,
        confidence2:
            layout.confidence2 === undefined
                ? prev.confidence2
                : toConfidence2Pct(layout.confidence2),
    };
}

/**
 * Re-coerce the confidence keys of an already-merged settings object — used where
 * settings arrive wholesale (localStorage hydration, a DANI layout's `settings` blob)
 * and may carry a fraction written by an older session.
 */
export function normalizeConfidenceSettings<
    T extends { confidence?: unknown; confidence2?: unknown },
>(merged: T, fallbackConfidence: number): T & ConfidencePair {
    return {
        ...merged,
        confidence: toConfidencePct(merged.confidence, fallbackConfidence),
        confidence2: toConfidence2Pct(merged.confidence2),
    };
}

/**
 * The legend describes the numbers in the file, so the stat keys come from the
 * committed (last Run) settings — the same source `computeFromApi` reads — while
 * everything else on the export stays live.
 *
 * Deliberately narrow: exactly the compute-relevant significance keys (M4S-5777 adds the
 * correction). `bannerRenames`, `sigStyle`, `decimalPlaces` and `showBase` are Run-independent by
 * design, so a wholesale substitution of the committed settings would silently drop a banner
 * rename made after the last Run from every exported file.
 */
export function exportSettingsFor<S extends string>(
    live: {
        confidence: number;
        confidence2: number | null;
        sigScope: S;
        sigCorrection?: unknown;
        suppressionThreshold?: unknown;
        referenceCols?: unknown;
    },
    committed?: {
        confidence: number;
        confidence2: number | null;
        sigScope: S;
        sigCorrection?: unknown;
        suppressionThreshold?: unknown;
        referenceCols?: unknown;
    } | null,
): {
    confidence: number;
    confidence2: number | null;
    sigScope: S;
    sigCorrection: SigCorrection;
    /**
     * M4S-5777 (X76): the grey threshold the cells were marked by in the last Run, which is the
     * suppression threshold (X12, base-thresholds.ts), under the name every reader of it uses.
     */
    lowBaseThreshold: number;
    /** M4S-5777 (X72): the reference columns the last Run tested against. */
    referenceCols: string[];
} {
    const src = committed ?? live;
    const low =
        typeof src.suppressionThreshold === 'number'
            ? src.suppressionThreshold
            : DEFAULT_SUPPRESSION;
    return {
        confidence: src.confidence,
        confidence2: src.confidence2,
        sigScope: src.sigScope,
        sigCorrection: toSigCorrection(src.sigCorrection),
        lowBaseThreshold: low,
        referenceCols: Array.isArray(src.referenceCols)
            ? src.referenceCols.filter((c): c is string => typeof c === 'string')
            : [],
    };
}

/** The test each kind of row takes (M4S-5777, X68 / X80), as the hover and the legend name it. */
export const TEST_NAMES = {
    proportion: 'column-proportions z-test',
    mean: 't-test on means',
    nps: 'z-test on the NPS score (promoters and detractors as one sample)',
} as const;

export type SigTestKind = keyof typeof TEST_NAMES;

/** Which test a row's marks come from. */
export function testKindOfRow(row: { kind: string; netType?: string; statType?: string }) {
    if (row.netType === 'nps') return 'nps' as const;
    if (row.kind === 'stat' && row.statType === 'mean') return 'mean' as const;
    return 'proportion' as const;
}

/** What a scope compares, in the words the hover uses. */
function comparedWith(scope: string | undefined): string {
    if (scope === 'all') return 'compared with every column';
    if (scope === 'reference') return 'compared with its group’s reference column';
    return 'compared within its banner group';
}

/**
 * The hover on a cell's letters or arrow (M4S-5777, X82): what it was compared with, at which
 * level, by which test, two-sided. One sentence per mark, so every letter on screen explains itself.
 *
 *  - letters: "Significantly higher than B (Female) at 95% confidence · column-proportions z-test,
 *    two-sided · compared within its banner group".
 *  - lowercase: the same at the second level.
 *  - an arrow: "Significantly above the Total at 95% confidence · column-proportions z-test against
 *    the Total that contains it (part–whole), two-sided".
 */
export function sigTooltip(o: {
    mark: 'letters' | 'letters2' | 'up' | 'down';
    letters?: readonly string[];
    letterMap?: Readonly<Record<string, string>>;
    confidence: number | null | undefined;
    confidence2?: number | null;
    sigScope?: string;
    test: SigTestKind;
    correction?: unknown;
}): string {
    const correction = toSigCorrection(o.correction);
    const corrected =
        correction === 'none' ? '' : ` · corrected (${SIG_CORRECTION_LABELS[correction]})`;
    const test = TEST_NAMES[o.test];
    if (o.mark === 'up' || o.mark === 'down') {
        return `Significantly ${o.mark === 'up' ? 'above' : 'below'} the Total at ${o.confidence}% confidence · ${test} against the Total that contains it (part–whole), two-sided${corrected}`;
    }
    const level = o.mark === 'letters' ? o.confidence : o.confidence2;
    const names = (o.letters ?? [])
        .map((l) => {
            const label = o.letterMap?.[l.toUpperCase()];
            return label ? `${l.toUpperCase()} (${label})` : l.toUpperCase();
        })
        .join(', ');
    return `Significantly higher than ${names} at ${level}% confidence · ${test}, two-sided · ${comparedWith(o.sigScope)}${corrected}`;
}

/**
 * The significance legend printed under the header rows of both Excel exports.
 *
 * A STRICT printer: it prints the confidence it is given and does no unit guessing.
 * Feed it 0.95 and it prints "1% confidence" — cleaning up unit confusion is the
 * job of the boundary coercions above, not of the legend.
 *
 * M4S-5777: it names the marks the file prints (`sigMarksOf`: letters, or the vs-Total arrows),
 * and, when the caller says (every export passes `sigScope` through `exportSettingsFor`), what was
 * compared, how shared respondents were tested and any correction for many comparisons. Called
 * without a scope it prints the four lines it always printed.
 *
 * Returns every line including the explanatory sentence; returns [] when there is
 * no legend to print, so the caller adds no row at all.
 */
export function buildSigLegendLines(s: {
    sigStyle?: string;
    confidence?: number | null;
    confidence2?: number | null;
    sigScope?: string;
    sigCorrection?: unknown;
    /**
     * M4S-5777 X67: the crosstab's statistics standard, as `describeStandard` words it. Given, it is
     * the legend's last line, and it is printed even when the file carries no significance marks,
     * so every file says which standard its numbers follow. Absent, the legend is as before.
     */
    statsStandard?: string | null;
    /**
     * M4S-5777 (X76): the grey threshold of the last Run, and whether low cells are hidden. Given,
     * the legend says what grey (or ‡) means, so every file marks low bases the way the screen does.
     */
    lowBaseThreshold?: number | null;
    suppressionEnabled?: boolean;
    /** M4S-5777 (X81): an index sub-row is printed. */
    showIndex?: boolean;
}): string[] {
    const lines = [...significanceLines(s), ...statisticsLines(s)];
    const standard = s.statsStandard?.trim();
    if (!standard) return lines;
    return lines.length ? [...lines, `  ${standard}`] : [standard];
}

/** The legend's lines on low bases and the index (M4S-5777, X76, X81). */
function statisticsLines(s: {
    lowBaseThreshold?: number | null;
    suppressionEnabled?: boolean;
    showIndex?: boolean;
}): string[] {
    const lines: string[] = [];
    if (typeof s.lowBaseThreshold === 'number' && s.lowBaseThreshold > 0) {
        lines.push(`  ${lowBaseLegendLine(s.lowBaseThreshold, !!s.suppressionEnabled)}`);
    }
    if (s.showIndex) {
        lines.push('  [n] = index vs. Total: the column’s percentage over the Total’s, × 100.');
    }
    return lines.length ? ['Notes on the figures:', ...lines] : [];
}

/** M4S-5777 (X68, X72, X80, X82): which test each figure takes, and that every test is two-sided. */
const TESTS_LINE = `  Tests: percentages, ${TEST_NAMES.proportion}; Means, ${TEST_NAMES.mean}; NPS Score, ${TEST_NAMES.nps}. All tests are two-sided.`;

function significanceLines(s: {
    sigStyle?: string;
    confidence?: number | null;
    confidence2?: number | null;
    sigScope?: string;
    sigCorrection?: unknown;
}): string[] {
    const marks = sigMarksOf(s.sigStyle, s.sigScope);
    if (!marks.letters && !marks.totalArrows) return [];
    const conf1 = s.confidence;
    const conf2 = s.confidence2;
    if (!conf1 && !conf2) return [];
    const correction = toSigCorrection(s.sigCorrection);
    const correctionLine =
        correction === 'none'
            ? []
            : [
                  `  Corrected for many comparisons: ${SIG_CORRECTION_LABELS[correction]}, within each row and banner group.`,
              ];

    if (marks.totalArrows) {
        const lines: string[] = ['Significance testing:'];
        if (conf1) {
            lines.push(
                `  ▲ / ▼ = significantly above / below the Total column at ${Math.round(conf1)}% confidence.`,
            );
        }
        lines.push('  Each column is tested against the Total that contains it (part–whole test).');
        if (s.sigScope !== undefined) lines.push(TESTS_LINE);
        return [...lines, ...correctionLine];
    }

    const lines: string[] = ['Significance testing:'];
    if (conf1) lines.push(`  UPPERCASE letters (A, B, C…) = ${Math.round(conf1)}% confidence`);
    if (conf2) lines.push(`  lowercase letters (a, b, c…) = ${Math.round(conf2)}% confidence`);
    lines.push(
        '  A letter in a cell means this cell is significantly higher than the column indicated by the letter.',
    );
    if (s.sigScope === undefined) return lines;
    lines.push(
        s.sigScope === 'all'
            ? '  Compared: every column with every other column.'
            : s.sigScope === 'reference'
              ? '  Compared: each column with its banner group’s reference (control) column; a group without one, within the group. Lift = the difference from the reference.'
              : '  Compared: columns within each banner group.',
        '  Columns that share respondents are tested with the overlap (dependent-sample) correction.',
        TESTS_LINE,
    );
    return [...lines, ...correctionLine];
}
